/**
 * Accelerator App format definitions.
 *
 * Each format describes a standardized application template that can be generated
 * from canvas data. The registry maps format IDs to metadata used by the UI,
 * generators, and evaluation engine.
 */

export const APP_FORMAT_IDS = {
  YC: "yc",
  TECHSTARS: "techstars",
  PLUG_AND_PLAY: "plug-and-play",
} as const;

export type AppFormatId =
  (typeof APP_FORMAT_IDS)[keyof typeof APP_FORMAT_IDS];

export const APP_FORMATS: Record<
  AppFormatId,
  {
    id: AppFormatId;
    label: string;
    shortDescription: string;
    description: string;
    iconName: string;
    enabled: boolean;
  }
> = {
  [APP_FORMAT_IDS.YC]: {
    id: APP_FORMAT_IDS.YC,
    label: "YC Application",
    shortDescription: "Y Combinator",
    description:
      "Direct, jargon-free application covering what you're building, users, traction, and team.",
    iconName: "Rocket",
    enabled: true,
  },
  [APP_FORMAT_IDS.TECHSTARS]: {
    id: APP_FORMAT_IDS.TECHSTARS,
    label: "Techstars Application",
    shortDescription: "Techstars",
    description:
      "Elevator pitch, problem/solution, market, traction, and why Techstars.",
    iconName: "Star",
    enabled: true,
  },
  [APP_FORMAT_IDS.PLUG_AND_PLAY]: {
    id: APP_FORMAT_IDS.PLUG_AND_PLAY,
    label: "Plug and Play",
    shortDescription: "Plug and Play Tech Center",
    description:
      "Enterprise-focused: corporate co-innovation, vertical alignment, and pilot potential.",
    iconName: "Plug",
    enabled: true,
  },
};

/** Coaching copy in the detail view that names who reads the application. */
export interface AccelAppCoachingCopy {
  scoresIntro: string;
  suggestionsIntro: string;
  oneLinerIntro: string;
  /** "How big is your vision?" subtitle: one line, under the heading. */
  visionTeaser: string;
  /** The interview explanation above the founder's vision and the slider. */
  visionPractice: string;
  keepBuilding: string;
}

const YC_COACHING_COPY: AccelAppCoachingCopy = {
  scoresIntro:
    "Lev scored each section the way YC partners evaluate applications: specificity, honesty, and evidence over polish. The strongest applications are direct about what's working and honest about what's early.",
  suggestionsIntro:
    "These are the gaps a YC partner would notice. Lev identified them so you can address them before anyone else reads your application. Answer a question, hit Recalculate, and watch your score climb.",
  oneLinerIntro:
    "Your one-liner is the first thing a YC partner reads. It determines whether they keep going. Lev generated five options in different styles. Pick the one that feels most like you, then hit Recalculate and it will lead your application.",
  visionTeaser: "The strongest YC applications think big.",
  visionPractice:
    "In interviews, expect questions like \u201cwhy isn't this 10x bigger?\u201d or \u201cwhat happens if this actually works?\u201d Their job is to find the massive outcome hiding in your idea. Let's practice now. Drag the slider from spark toward supernova and see the version of your story a partner would get genuinely excited about. Same company, bolder frame.",
  keepBuilding:
    "Your YC application is just the beginning. Lev canvas goes deeper on every section you just wrote: find customers, map competitors, size your market, run GTM experiments, develop your product, and build your team.",
};

/**
 * Coaching copy for any program other than YC, from two words of its spec.
 * `reviewerNoun` MUST pluralize by adding "s" ("Techstars reviewer" becomes
 * "Techstars reviewers"); its article is "an" before a vowel letter, "a"
 * otherwise. With no `programName` the copy names no program.
 */
export function programCoachingCopy({
  programName,
  reviewerNoun,
}: {
  programName?: string;
  reviewerNoun: string;
}): AccelAppCoachingCopy {
  const aReviewer = `${/^[aeiou]/i.test(reviewerNoun) ? "an" : "a"} ${reviewerNoun}`;
  const programApplications = programName ? `${programName} applications` : "applications";
  const yourApplication = programName ? `Your ${programName} application` : "Your application";
  return {
    scoresIntro: `Lev scored each section the way ${reviewerNoun}s evaluate applications: specificity, honesty and evidence over polish. The strongest applications are direct about what's working and honest about what's early.`,
    suggestionsIntro: `These are the gaps ${aReviewer} would notice. Lev identified them so you can address them before anyone else reads your application. Answer a question, hit Recalculate, and watch your score climb.`,
    oneLinerIntro: `Your one-liner is the first thing ${aReviewer} reads, and it decides whether they keep going. Lev generated five options in different styles. Pick the one that feels most like you, then hit Recalculate and it will lead your application.`,
    visionTeaser: `The strongest ${programApplications} think big.`,
    visionPractice: `In an interview, expect questions like \u201cwhy isn't this 10x bigger?\u201d or \u201cwhat happens if this actually works?\u201d Let's practice now. Drag the slider from spark toward supernova and see the version of your story a reviewer would get genuinely excited about. Same company, bolder frame.`,
    keepBuilding: `${yourApplication} is just the beginning. Lev canvas goes deeper on every section you just wrote: find customers, map competitors, size your market, run GTM experiments, develop your product, and build your team.`,
  };
}

/**
 * Per-format coaching copy. A format without its own entry gets
 * NEUTRAL_COACHING_COPY, never YC's: YC's names YC partners. A program entry
 * MUST NOT name YC, and MUST NOT imply the program reviews or endorses
 * applications through Lev.
 */
const ACCEL_APP_COACHING_COPY: Partial<Record<AppFormatId, AccelAppCoachingCopy>> = {
  [APP_FORMAT_IDS.YC]: YC_COACHING_COPY,
  [APP_FORMAT_IDS.TECHSTARS]: programCoachingCopy({ programName: "Techstars", reviewerNoun: "Techstars reviewer" }),
};

const NEUTRAL_COACHING_COPY = programCoachingCopy({ reviewerNoun: "reviewer" });

export function getAccelAppCoachingCopy(formatId: AppFormatId = APP_FORMAT_IDS.YC): AccelAppCoachingCopy {
  return ACCEL_APP_COACHING_COPY[formatId] ?? NEUTRAL_COACHING_COPY;
}

/**
 * Resolves a raw `?format=` query param into a format id the apps page may
 * preselect. Accepts only ids in `launchedIds` (the viewer's launch set, see
 * src/lib/accelerators/launchedFormats.ts); unknown and unlaunched ids fall back
 * to the format picker — never trust the param to open a builder that isn't
 * live (LEV-2229).
 */
export function resolveInitialAppFormat(
  formatParam: string | null,
  launchedIds: readonly AppFormatId[],
): AppFormatId | null {
  if (formatParam === null) return null;
  return launchedIds.find((id) => id === formatParam) ?? null;
}

/**
 * The Accelerator Apps page with a program's format preselected, read back by
 * resolveInitialAppFormat: the page re-validates the id against the viewer's
 * launched formats, so an unlaunched or stale id falls back to the picker.
 */
export function buildDraftApplicationHref(projectId: string, appFormatId: string): string {
  return `/${projectId}/labs/accelerator-apps?format=${appFormatId}`;
}

/**
 * The refusal a founder gets past the brutal-read limit (RATE_LIMITS
 * .acceleratorBrutalRead), shared by per-answer Brutal mode and the
 * whole-application read. Founder-facing: no em-dashes.
 */
export const BRUTAL_READ_RATE_LIMIT_MESSAGE =
  "That's a lot of brutal reads in a few minutes. Give it a moment and try again.";

export const APP_STATUS = {
  DRAFT: "draft",
  GENERATING: "generating",
  READY: "ready",
  EVALUATING: "evaluating",
  ERROR: "error",
} as const;

/**
 * `errorMessage` of a row whose last run the founder cancelled. On a first
 * draft (status DRAFT, no version) an automatic start must not read the row
 * as unstarted and spend a draft they did not ask for: a DRAFT row's
 * `errorMessage` is null or this mark. On a READY row it tells a cancelled
 * score apart from a failed one, so the panel says "Scoring cancelled."
 * instead of a failure. It is never a run marker (APP_RUN_MARKER_PREFIX), so
 * every claim, bind and fence reads it as "no run"; every claim replaces it.
 */
export const APP_CANCELLED_MARKER = "cancelled";

/**
 * Prefix of a running application's run marker: while a draft holds the row
 * GENERATING (LEV-2434), or a score holds it EVALUATING or READY awaiting the
 * score its draft chained to (LEV-2552), `errorMessage` is the marker of the
 * one run allowed to spend and save
 * (src/lib/inngest/accelerators/applications/currentRun.ts). Only the claims
 * (generate, answerSuggestions), cancel, the jobs' saves, the jobs'
 * onFailures and an upload replace it; any other write that clears
 * `errorMessage` mid-run silently throws that run away. It is never shown:
 * the panel renders `errorMessage` only on an ERROR row.
 */
export const APP_RUN_MARKER_PREFIX = "run:";

/**
 * How long a row may wait on the score of its current version. A scoring run
 * takes 30 to 60 seconds, up to two minutes with interview prep, and the
 * evaluate job retries once; past this, the run failed and the panel stops
 * waiting on it.
 */
export const SCORE_WAIT_MS = 5 * 60_000;

/**
 * Age (from the row's `updatedAt`) past which an EVALUATING row's run is
 * dead: a job that died without its onFailure never moves the row on. The
 * score claim (answerSuggestions) reclaims such a row and the panel offers
 * Try again past the same bound, so both sides MUST read this one constant:
 * a client cutoff earlier than the server's would offer a Try again the
 * server refuses. The margin over SCORE_WAIT_MS makes it unlikely that a
 * slow live run (queued behind evaluateApp's per-app concurrency of 1) is
 * reclaimed, but it does not prevent it.
 *
 * The reclaim stamps a new run token (currentRun.ts, LEV-2552), so a run
 * still alive past this bound can no longer bind, save, or free the row: it
 * never pays if it had not reached its paid step, and its score is discarded
 * if it had. Only the reclaim's run saves; an old run already inside its paid
 * call still pays once (the residual accepted at the score claim in
 * acceleratorApplications.ts).
 */
export const SCORE_RUN_STALE_MS = SCORE_WAIT_MS + 2 * 60_000;

export const SUGGESTION_PRIORITY = {
  CRITICAL: "critical",
  HIGH: "high",
  MEDIUM: "medium",
} as const;

export type SuggestionPriority =
  (typeof SUGGESTION_PRIORITY)[keyof typeof SUGGESTION_PRIORITY];

export const SUGGESTION_STATUS = {
  UNANSWERED: "unanswered",
  ANSWERED: "answered",
  SKIPPED: "skipped",
} as const;

export type SuggestionStatus =
  (typeof SUGGESTION_STATUS)[keyof typeof SUGGESTION_STATUS];

/** Typed shape of suggestions stored in AcceleratorAppEvaluation.suggestions JSON. */
export interface AppSuggestion {
  id: string;
  section: string;
  priority: SuggestionPriority;
  issue: string;
  suggestion: string;
  question: string;
  answerStatus: SuggestionStatus;
  founderAnswer?: string;
  mapsToCanvasField?: string;
  /**
   * The spec coaching category this suggestion answers, absent on a
   * weak-answer fix. `section` still names the category's question, so the
   * suggestion joins that question's Improve row.
   */
  category?: string;
}

/** Predicted YC interview question with coaching context. */
export interface InterviewQuestion {
  id: string;
  question: string;
  why: string;
  suggestedAnswer: string;
  founderAnswer?: string;
}

/** Typed shape of section scores stored in AcceleratorAppEvaluation.sectionScores JSON. */
export interface AppSectionScore {
  section: string;
  score: number;
  rationale: string;
}
